import React, { createContext, useContext, useState, useEffect } from 'react';
import { usePostHog } from 'posthog-js/react';

const AnalyticsContext = createContext(null);

export const AnalyticsProvider = ({ children }) => {
  const posthog = usePostHog();
  const [pendingEvents, setPendingEvents] = useState([]);
  
  // Process any events that were dispatched before context was ready
  useEffect(() => {
    if (posthog && pendingEvents.length > 0) {
      pendingEvents.forEach(event => {
        processEvent(event, posthog);
      });
      setPendingEvents([]);
    }
  }, [posthog, pendingEvents]);
  
  const processEvent = (event, posthogInstance) => {
    const { type, payload } = event;
    
    switch (type) {
      case 'track_page_view':
        trackPageView(payload.canonicalPath, payload.previousPath, posthogInstance);
        break;
      case 'track_user':
        trackUser(payload.user, posthogInstance);
        break;
      case 'track_checkout':
        trackCheckout(payload.event, payload.data, posthogInstance);
        break;
      case 'track_user_engagement':
        trackUserEngagement(payload.eventName, payload.eventData, posthogInstance);
        break;
      default:
        console.warn(`Unknown analytics event type: ${type}`);
    }
  };
  
  const dispatchAnalyticsEvent = (event) => {
    if (!posthog) {
      // Queue event for when PostHog is available
      setPendingEvents(prev => [...prev, event]);
      return;
    }
    
    // Process event immediately
    processEvent(event, posthog);
  };
  
  // Handler functions ported from handlers.js
  
  // Track page views
  const trackPageView = (canonicalPath, previousPath, posthogInstance) => {
    const ph = posthogInstance || posthog;
    if (ph && previousPath) {
      // Only manually capture page views for in-app navigation
      // Initial page view is captured automatically by PostHog
      ph.capture('$pageview', {
        $current_url: canonicalPath,
        previous_path: previousPath,
      });
    }
  };
  
  // Track user identification and properties
  const trackUser = (user, posthogInstance) => {
    const ph = posthogInstance || posthog;
    if (!ph || !user?.id) {
      return;
    }

    // Identify user with their ID
    ph.identify(String(user.id.uuid), {
      email: user.attributes?.email,
      name: user.attributes?.profile?.displayName,
      user_type: user.attributes?.profile?.publicData?.userType || 'customer',
      email_verified: !!user.attributes?.emailVerified,
      has_stripe_account: !!user.attributes?.stripeConnected,
      preferred_language: user.attributes?.profile?.preferredLanguage,
    });

    // Calculate profile completion percentage
    const getProfileCompletionStatus = (profile) => {
      if (!profile) return 0;
      
      const steps = {
        has_avatar: !!profile.publicData?.avatar,
        has_bio: !!profile.bio,
        has_location: !!profile.publicData?.location,
        email_verified: !!user.attributes?.emailVerified
      };
      
      const completed = Object.values(steps).filter(Boolean).length;
      const total = Object.keys(steps).length;
      return Math.round((completed / total) * 100);
    };

    // Set user properties
    ph.people.set({
      profile_completion: getProfileCompletionStatus(user.attributes?.profile),
      account_created_at: user.attributes?.createdAt,
      country: user.attributes?.profile?.publicData?.location?.country,
      has_listings: user.attributes?.profile?.publicData?.hasListings || false,
      email: user.attributes?.email,
      name: user.attributes?.profile?.displayName,
      user_type: user.attributes?.profile?.publicData?.userType || 'customer',
      email_verified: !!user.attributes?.emailVerified,
      has_stripe_account: !!user.attributes?.stripeConnected,
      preferred_language: user.attributes?.profile?.preferredLanguage,
    });
  };
  
  // Track checkout events
  const trackCheckout = (event, data, posthogInstance) => {
    const ph = posthogInstance || posthog;
    if (!ph) {
      return;
    }

    const safeData = {
      ...data,
      timestamp: new Date().toISOString(),
    };

    // Remove any potential PII that we don't want to track
    delete safeData.inquiryMessage;

    switch (event) {
      case 'begin_checkout':
        ph.capture('begin_checkout', {
          listing_id: safeData.listingId,
          listing_type: safeData.listingType,
          process_name: safeData.processName,
          currency: safeData.currency,
          value: safeData.price
        });
        break;

      case 'inquiry_submitted':
        ph.capture('inquiry_submitted', {
          listing_id: safeData.listingId,
          listing_type: safeData.listingType,
          process_name: safeData.processName,
          currency: safeData.currency,
          value: safeData.price
        });
        break;

      case 'inquiry_error':
        ph.capture('inquiry_error', {
          listing_id: safeData.listingId,
          error_type: safeData.errorType,
          error_message: safeData.errorMessage
        });
        break;
    }
  };
  
  // Track user engagement events
  const trackUserEngagement = (eventName, eventData = {}, posthogInstance) => {
    const ph = posthogInstance || posthog;
    if (!ph) {
      return;
    }

    // Ensure no PII is included in the event data
    const safeEventData = {
      ...eventData,
      timestamp: new Date().toISOString(),
    };

    // Remove any potential PII fields
    delete safeEventData.email;
    delete safeEventData.name;
    delete safeEventData.phone;
    delete safeEventData.address;

    ph.capture(eventName, safeEventData);
  };
  
  return (
    <AnalyticsContext.Provider value={{ dispatchAnalyticsEvent }}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => useContext(AnalyticsContext);

// Helper hook for tracking page views in components
export const usePageView = (canonicalPath) => {
  const { dispatchAnalyticsEvent } = useAnalytics();
  const prevPathRef = React.useRef(null);
  
  useEffect(() => {
    if (canonicalPath && prevPathRef.current !== canonicalPath) {
      dispatchAnalyticsEvent({
        type: 'track_page_view',
        payload: {
          canonicalPath,
          previousPath: prevPathRef.current
        }
      });
      prevPathRef.current = canonicalPath;
    }
  }, [canonicalPath, dispatchAnalyticsEvent]);
};

// Helper hook for tracking user engagement in components
export const useTrackEvent = () => {
  const { dispatchAnalyticsEvent } = useAnalytics();
  
  return {
    trackEvent: (eventName, eventData = {}) => {
      dispatchAnalyticsEvent({
        type: 'track_user_engagement',
        payload: {
          eventName,
          eventData
        }
      });
    },
    trackCheckout: (event, data) => {
      dispatchAnalyticsEvent({
        type: 'track_checkout',
        payload: {
          event,
          data
        }
      });
    }
  };
}; 